import { useMemo } from 'react';
import { usePermissions } from './usePermissions';
import { PolicyTypes } from '@flashbacktech/flashbackclient/api';

export interface PolicyPermissions {
  // Global access levels
  canManageOrgPolicies: boolean;        // OWNER or ADMINISTRATORS
  canManageAllWorkspaces: boolean;      // OWNER, ADMINISTRATORS, or WORKSPACES
  
  // Workspace-specific
  canAccessWorkspace: (workspaceId: string) => boolean;
  canManageWorkspacePolicies: (workspaceId: string) => boolean;
  
  // Repo-specific (inherits from workspace)
  canManageRepoPolicies: (workspaceId: string, repoId: string) => boolean;
  
  // Policy-specific (for edit/delete buttons)
  canManagePolicy: (policy: PolicyTypes.PolicyDTO) => boolean;
  
  // For determining what scope the user can create at
  canCreateAtScope: (scope: { workspaceId?: string; repoId?: string }) => boolean;
  
  // Check if user has any policy access at all
  hasAnyPolicyAccess: boolean;
}

/**
 * Hook to check user permissions specifically for policy management.
 * 
 * This builds on top of `usePermissions` with policy-specific logic.
 * 
 * Permission rules:
 * - OWNER (0xff) or ADMINISTRATORS (0xfe): Can manage org-level policies and all workspace/repo policies
 * - WORKSPACES (0x02): Can manage all workspace and repo policies (but not org-level)
 * - Workspace ADMIN: Can manage policies for their specific workspace and its child repos
 * - Others: No policy access
 * 
 * @example
 * ```tsx
 * import { usePolicyPermissions } from '../hooks/usePolicyPermissions';
 * 
 * function PolicyList() {
 *   const permissions = usePolicyPermissions();
 *   
 *   // Check if user can create org-level policies
 *   if (permissions.canManageOrgPolicies) {
 *     // Show org policy creation button
 *   }
 *   
 *   // Check if user can manage a specific policy
 *   const canEdit = permissions.canManagePolicy(policy);
 * }
 * ```
 */
export const usePolicyPermissions = (): PolicyPermissions => {
  const basePermissions = usePermissions();
  
  const policyPermissions = useMemo<PolicyPermissions>(() => {
    const { isOrgManager, canManageWorkspaces, isWorkspaceAdmin, workspaces } = basePermissions;
    
    // Org-level management: OWNER or ADMINISTRATORS only
    const canManageOrgPolicies = isOrgManager;
    
    // All workspaces management: OWNER, ADMINISTRATORS, or WORKSPACES role
    const canManageAllWorkspaces = canManageWorkspaces;
    
    // Check if user can access a workspace for policies (only ADMIN can see policies)
    const canAccessWorkspace = (workspaceId: string): boolean => {
      if (canManageAllWorkspaces) return true;
      return isWorkspaceAdmin(workspaceId);
    };
    
    // Check if user can manage policies in a specific workspace
    const canManageWorkspacePolicies = (workspaceId: string): boolean => {
      if (canManageAllWorkspaces) return true;
      return isWorkspaceAdmin(workspaceId);
    };
    
    // Check if user can manage policies for a repo (inherits from workspace)
    const canManageRepoPolicies = (workspaceId: string, _repoId: string): boolean => {
      // Repo policy management follows workspace permissions
      return canManageWorkspacePolicies(workspaceId);
    };
    
    // Check if user can manage a specific policy
    const canManagePolicy = (policy: PolicyTypes.PolicyDTO): boolean => {
      // Org-level policy
      if (!policy.workspaceId && !policy.repoId) {
        return canManageOrgPolicies;
      }
      
      // Workspace or repo-level policy
      if (policy.workspaceId) {
        return canManageWorkspacePolicies(policy.workspaceId);
      }
      
      return false;
    };
    
    // Check if user can create policies at a given scope
    const canCreateAtScope = (scope: { workspaceId?: string; repoId?: string }): boolean => {
      // Org-level (no workspace, no repo)
      if (!scope.workspaceId && !scope.repoId) {
        return canManageOrgPolicies;
      }
      
      // Workspace or repo level
      if (scope.workspaceId) {
        return canManageWorkspacePolicies(scope.workspaceId);
      }
      
      return false;
    };
    
    // Check if user has any policy access at all
    const hasAnyPolicyAccess = canManageOrgPolicies || 
      canManageAllWorkspaces || 
      workspaces.some(w => w.userRole === 'ADMIN');
    
    return {
      canManageOrgPolicies,
      canManageAllWorkspaces,
      canAccessWorkspace,
      canManageWorkspacePolicies,
      canManageRepoPolicies,
      canManagePolicy,
      canCreateAtScope,
      hasAnyPolicyAccess,
    };
  }, [basePermissions]);
  
  return policyPermissions;
};

export default usePolicyPermissions;
